import { useEffect, useState } from 'react';
import {
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
  Smartphone,
  UserRound,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginWithCredentials, verifyLoginMfa } from '../api';
import { normalizeLoginResponse, normalizeMfaResponse } from '../auth/authResponse';
import { getSafeInternalRoute } from '../auth/portalEntry';
import { useAuth } from '../auth/useAuth';

const getLoginErrorMessage = (error) => {
  if (error?.status === 401) return 'The username or password is incorrect.';
  if (error?.status === 403) return 'This account cannot log in yet. Please verify it in the Miggus app.';
  if (error?.status === 400) return 'Please check your app username and password and try again.';
  if (error instanceof TypeError) return 'We could not connect to Miggus. Check your connection and try again.';
  return 'We could not log you in. Please try again.';
};

export default function LoginScreen() {
  const { authenticate } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [initialNavigationState] = useState(() => location.state);
  const [returnTo] = useState(() => getSafeInternalRoute(location.state?.from));
  const [showExpirationNotice, setShowExpirationNotice] = useState(
    location.state?.authNotice === 'session_expired',
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phase, setPhase] = useState('credentials');
  const [challenge, setChallenge] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialNavigationState) {
      navigate('/login', { replace: true, state: null });
    }
  }, [initialNavigationState, navigate]);

  useEffect(() => {
    if (!showExpirationNotice) return undefined;
    const timeout = window.setTimeout(() => setShowExpirationNotice(false), 5000);
    return () => window.clearTimeout(timeout);
  }, [showExpirationNotice]);

  const finishLogin = (session) => {
    authenticate(session);
    navigate(returnTo, { replace: true, state: null });
  };

  const handleCredentialSubmit = async (event) => {
    event.preventDefault();
    const trimmedUsername = username.trim();
    if (!trimmedUsername || !password) {
      setError('Enter your app username and password.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const response = await loginWithCredentials(trimmedUsername, password);
      const result = normalizeLoginResponse(response, trimmedUsername);
      setPassword('');

      if (result.type === 'challenge') {
        setChallenge(result);
        setVerificationCode('');
        setPhase('mfa');
        return;
      }

      finishLogin(result.session);
    } catch (loginError) {
      setError(getLoginErrorMessage(loginError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(verificationCode) || !challenge) {
      setError('Enter the six-digit code from your authenticator app.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const response = await verifyLoginMfa({
        username: challenge.username,
        session: challenge.session,
        code: verificationCode,
      });
      finishLogin(normalizeMfaResponse(response, challenge.username));
    } catch (verificationError) {
      if (verificationError?.status === 400) {
        setPhase('credentials');
        setChallenge(null);
        setVerificationCode('');
        setError('The verification session expired. Please enter your credentials again.');
      } else {
        setError(verificationError?.status === 401
          ? 'The verification code is incorrect. Please try again.'
          : 'We could not verify the code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const returnToCredentials = () => {
    setPhase('credentials');
    setChallenge(null);
    setVerificationCode('');
    setError('');
  };

  return (
    <main className="relative min-h-[100dvh] overflow-hidden bg-gradient-to-b from-[#faf5ff] to-[#f3e8ff] px-4 py-8 font-sans sm:py-12">
      <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-[#ddd6fe]/70 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#eadcff]/80 blur-3xl" />

      <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-md flex-col justify-center sm:min-h-[calc(100dvh-6rem)]">
        {showExpirationNotice && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-900 shadow-sm"
          >
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="flex-1 font-medium">
              Your token has expired. We moved you to login. Please log in again with your app credentials.
            </p>
            <button
              type="button"
              onClick={() => setShowExpirationNotice(false)}
              className="rounded-md p-1 text-amber-700 transition hover:bg-amber-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
              aria-label="Dismiss session notice"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        <section className="rounded-3xl border border-white/80 bg-white/90 p-6 shadow-[0_28px_70px_-28px_rgba(88,28,135,0.4)] backdrop-blur-xl sm:p-8">
          <header className="mb-7 text-center">
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-[24px] bg-gradient-to-br from-[#7c3aed] to-[#9333ea] text-white shadow-[0_14px_30px_-10px_rgba(124,58,237,0.75)]">
              <Smartphone className="h-9 w-9" aria-hidden="true" />
            </div>
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#7c3aed]">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Miggus Billing Portal
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
              {phase === 'mfa' ? 'Verify your login' : 'Welcome back'}
            </h1>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              {phase === 'mfa'
                ? `Enter the authenticator code for ${challenge?.username || 'your account'}.`
                : 'Please log in with your app credentials to continue.'}
            </p>
          </header>

          {error && (
            <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          {phase === 'credentials' ? (
            <form onSubmit={handleCredentialSubmit} className="space-y-5" noValidate>
              <div>
                <label htmlFor="login-username" className="mb-2 block text-sm font-semibold text-gray-800">
                  Username or mobile number
                </label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <input
                    id="login-username"
                    name="username"
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck="false"
                    disabled={isSubmitting}
                    className="h-13 w-full rounded-xl border border-gray-200 bg-white py-3 pl-12 pr-4 text-[15px] text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#9333ea] focus:ring-4 focus:ring-purple-100 disabled:cursor-not-allowed disabled:bg-gray-50"
                    placeholder="Enter your app username"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="mb-2 block text-sm font-semibold text-gray-800">
                  Password
                </label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    className="h-13 w-full rounded-xl border border-gray-200 bg-white py-3 pl-12 pr-12 text-[15px] text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#9333ea] focus:ring-4 focus:ring-purple-100 disabled:cursor-not-allowed disabled:bg-gray-50"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    disabled={isSubmitting}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-400 transition hover:bg-purple-50 hover:text-[#7c3aed] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#9333ea] px-6 py-3.5 text-base font-bold text-white shadow-[0_12px_24px_-10px_rgba(124,58,237,0.75)] transition hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting && <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {isSubmitting ? 'Logging in…' : 'Log in'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-5" noValidate>
              <div>
                <label htmlFor="login-code" className="mb-2 block text-sm font-semibold text-gray-800">
                  Six-digit authenticator code
                </label>
                <input
                  id="login-code"
                  name="one-time-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  disabled={isSubmitting}
                  className="h-14 w-full rounded-xl border border-gray-200 bg-white px-4 text-center font-mono text-2xl font-bold tracking-[0.45em] text-gray-900 outline-none transition focus:border-[#9333ea] focus:ring-4 focus:ring-purple-100 disabled:bg-gray-50"
                  aria-describedby="code-help"
                  autoFocus
                />
                <p id="code-help" className="mt-2 text-center text-xs text-gray-500">
                  Open your authenticator app to find the current code.
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || verificationCode.length !== 6}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#9333ea] px-6 py-3.5 text-base font-bold text-white shadow-[0_12px_24px_-10px_rgba(124,58,237,0.75)] transition hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting && <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {isSubmitting ? 'Verifying…' : 'Verify and continue'}
              </button>
              <button
                type="button"
                onClick={returnToCredentials}
                disabled={isSubmitting}
                className="w-full rounded-xl px-4 py-2 text-sm font-semibold text-[#7c3aed] transition hover:bg-purple-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed] disabled:opacity-50"
              >
                Use different credentials
              </button>
            </form>
          )}
        </section>

        <p className="mt-6 text-center text-xs text-gray-400">
          © 2026 Genaiology Inc. All rights reserved.
        </p>
      </div>
    </main>
  );
}
